import React, { useMemo, useState } from 'react';
import { Button, Empty, Input, Space, Spin, Table, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { OrderDto } from '../../../dto/order';
import { getDisplayOrderNumber } from '../../../types/order';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { getStatusTagColor } from '../../../utils/orderStatus';
import { getOrderTableProductEntries } from '../../../pages/orderTableUtils';
import CustomerContactCell from '../../../components/customers/CustomerContactCell';
import { hasNonstandardWZOrderStatus } from '../api/wzCreateApi';

const { Text } = Typography;
const EMPTY_VALUE = '—';

interface WZOrderPickerProps {
  orders: OrderDto[];
  loading: boolean;
  onPick: (order: OrderDto) => void;
  onManual: () => void;
}

/*
 * Modeled on PZDetail's `renderSupplierOrderPZPicker` (transport → ZD picker),
 * but simpler: WZ/new opens directly in this order-picker mode, no
 * intermediate "how do you want to create this?" modal. One row = one real
 * customer order; an unusual lifecycle stage warns but never hides the row.
 * "Utwórz WZ" seeds the existing create form.
 */
const matchesSearch = (order: OrderDto, needle: string): boolean => {
  if (!needle) return true;
  const haystacks = [
    order.orderNumber,
    order.displayOrderNumber,
    order.customerName,
    order.customer?.name,
    order.clientOrderNumber,
  ];
  return haystacks.some((value) => (value || '').toLowerCase().includes(needle));
};

const renderProductsCell = (order: OrderDto) => {
  const entries = getOrderTableProductEntries(order.products as any, EMPTY_VALUE);

  if (entries.length === 0) {
    return <Text type="secondary">{EMPTY_VALUE}</Text>;
  }

  return (
    <div className="wz-order-picker__products-cell">
      {entries.map((entry) => (
        <div key={entry.key} className="wz-order-picker__product-row">
          <div className="wz-order-picker__product-main">
            <span className="wz-order-picker__product-name">{entry.name}</span>
            {entry.dimensionsLabel ? (
              <span className="wz-order-picker__product-dimensions">{entry.dimensionsLabel}</span>
            ) : null}
          </div>
          <span className="wz-order-picker__product-quantity">{entry.quantityLabel ?? ''}</span>
        </div>
      ))}
    </div>
  );
};

const WZOrderPicker: React.FC<WZOrderPickerProps> = ({ orders, loading, onPick, onManual }) => {
  const { t } = useTranslation(['wz', 'orders', 'common']);
  const [search, setSearch] = useState('');

  const filteredOrders = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return orders.filter((order) => matchesSearch(order, needle));
  }, [orders, search]);

  const columns = [
    {
      title: t('wz:pickOrderMode.columns.customer'),
      key: 'customer',
      width: '32%',
      render: (_: unknown, order: OrderDto) => (
        <Space direction="vertical" size={2}>
          <span className="wz-order-picker__customer-name">
            {order.customerName || order.customer?.name || EMPTY_VALUE}
          </span>
          <span className="wz-order-picker__order-meta">
            <span className="wz-order-picker__order-number">{getDisplayOrderNumber(order)}</span>
            {order.orderDate && (
              <span className="wz-order-picker__order-date">{formatDateEuropean(order.orderDate)}</span>
            )}
            <Tag color={getStatusTagColor(order.status)} className="wz-order-picker__status-tag">
              {t(`orders:statusOptions.${order.status}`, {
                defaultValue: t('common:unknownStatus'),
              })}
            </Tag>
          </span>
          {hasNonstandardWZOrderStatus(order) && (
            <Text type="warning" className="wz-order-picker__status-warning">
              {t('wz:pickOrderMode.nonstandardStatusWarning')}
            </Text>
          )}
          {/*
            OrderDto's Customer type does not carry `contactPeople` (only
            Orders.tsx's loosely-typed `any` row does) — pass through the
            two scalar fields it does have and let CustomerContactCell's own
            normalization build a single-contact fallback from them.
          */}
          <CustomerContactCell
            contactPerson={order.customer?.contactPerson}
            phone={order.customer?.phone}
            emptyValue={EMPTY_VALUE}
          />
        </Space>
      ),
    },
    {
      title: t('wz:pickOrderMode.columns.products'),
      key: 'products',
      render: (_: unknown, order: OrderDto) => renderProductsCell(order),
    },
    {
      title: t('wz:pickOrderMode.columns.reference'),
      key: 'reference',
      width: 140,
      render: (_: unknown, order: OrderDto) => (
        order.clientOrderNumber
          ? <span>{order.clientOrderNumber}</span>
          : <Text type="secondary">{EMPTY_VALUE}</Text>
      ),
    },
    {
      key: 'action',
      width: 130,
      render: (_: unknown, order: OrderDto) => (
        <Button
          type="primary"
          onClick={() => onPick(order)}
          data-testid={`wz-order-picker-create-${order.id}`}
        >
          {t('wz:pickOrderMode.createWZ')}
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Input.Search
        allowClear
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('wz:pickOrderMode.searchPlaceholder')}
        style={{ maxWidth: 480 }}
      />
      <Spin spinning={loading}>
        <Table
          className="wz-order-picker__table"
          tableLayout="fixed"
          rowKey="id"
          columns={columns}
          dataSource={filteredOrders}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical" size={4}>
                    <Text>{t('wz:pickOrderMode.empty')}</Text>
                    <Text type="secondary">{t('wz:pickOrderMode.emptyHint')}</Text>
                  </Space>
                }
              />
            ),
          }}
        />
      </Spin>
      <Button type="link" className="wz-order-picker__manual-link" onClick={onManual}>
        {t('wz:pickOrderMode.manualLink')}
      </Button>
    </Space>
  );
};

export default WZOrderPicker;
